import { useEffect, useState } from 'react';
import api from '../../../services/api';
import type { BoardroomProfile, BoardroomReportKey } from '../../../types';
import ProfileView from './ProfileView';
import useBoardRefresh from './useBoardRefresh';
import type { OnDig } from './MissingList';

/**
 * One thing in full, pinned to the board.
 *
 * The same dossier the profile stage draws — which is what a noticing means
 * when it says "this pain has eight voices and nothing addresses it": the
 * useful thing to put on screen is the pain itself, with what is under it.
 *
 * It fetches its own rows for `ListCardPane`'s reason: the page holds ONE
 * report at a time keyed by kind, and two profile cards share that kind. The
 * card's `subject` is what tells them apart, so the fetch belongs with the
 * card.
 *
 * THE SUBJECT IS NOT ALWAYS A COMPANY. `notices.py` hands a PAIN id, and
 * `build_profile` serves companies, products, solution types and everything
 * else — so this never assumes a kind, and neither does `ProfileView`.
 */
export default function ProfilePane({
  boardroomId,
  subject,
  onOpen,
  onEdit,
  onDig,
  digging,
  refreshKey,
}: {
  boardroomId: number;
  /** The item id this card is of, as a string — a card is a kind plus a subject. */
  subject: string;
  onOpen: (itemId: number) => void;
  onEdit: (itemId: number) => void;
  onDig: OnDig;
  digging: boolean;
  /** When the page drives refreshes, its key; otherwise the card listens itself. */
  refreshKey?: string | number;
}) {
  const [data, setData] = useState<BoardroomProfile | null>(null);
  // LIVE, LIKE THE OTHER CARDS. It loaded once when opened, so a dig landing
  // twenty voices and three products under a pain changed nothing on screen
  // until a manual reload.
  const fresh = useBoardRefresh(boardroomId, refreshKey === undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .getBoardroomReport<BoardroomProfile>(boardroomId, 'profile' as BoardroomReportKey, {
        subject,
      })
      .then(d => {
        if (alive) setData(d);
      })
      .catch(() => {
        // The item was killed or merged out from under the card.
        if (alive) setError('That item is not on this board any more.');
      });
    return () => {
      alive = false;
    };
  }, [boardroomId, subject, fresh, refreshKey]);

  if (error) {
    return <div className="text-xs text-gray-500 dark:text-gray-400">{error}</div>;
  }
  if (!data) {
    return <div className="text-xs text-gray-400 dark:text-gray-500">Reading the board…</div>;
  }
  return (
    <ProfileView
      data={data}
      onOpen={onOpen}
      onEdit={onEdit}
      onDig={onDig}
      digging={digging}
      loadFilings={itemId => api.getBoardroomItemFilings(boardroomId, itemId)}
    />
  );
}
